import { useChatStore, type UiMessage } from '../state/chatStore'
import { ContactCard, anliegenAlsFragen, kontaktAusAufruf } from './ContactCard'
import { useT } from '../lib/i18n'

/**
 * Which argument of each effectful tool the visitor must actually see before
 * agreeing. Without this the question ("Soll ich dir die Zusammenfassung
 * schicken?") looks identical whether the mail goes to the visitor or to an
 * address a manipulated document slipped into the arguments — so the consent
 * would be meaningless.
 */
const CONFIRM_DETAIL_FIELDS: Record<string, { field: string; label: string }[]> = {
  capture_lead: [
    { field: 'name', label: 'Name' },
    { field: 'email', label: 'E-Mail' },
    { field: 'company', label: 'Firma' },
  ],
  send_summary: [{ field: 'email', label: 'An' }],
  book_meeting: [
    { field: 'attendeeEmail', label: 'An' },
    { field: 'proposedStartIso', label: 'Termin' },
    { field: 'subject', label: 'Betreff' },
  ],
  call_staff: [{ field: 'reason', label: 'Grund' }],
  create_followup: [
    { field: 'questions', label: 'Anliegen' },
    { field: 'name', label: 'Name' },
    { field: 'company', label: 'Firma' },
    { field: 'phone', label: 'Telefon' },
    { field: 'email', label: 'E-Mail' },
  ],
}

export function confirmationDetails(
  name: string,
  input: Record<string, unknown>,
): { label: string; value: string }[] {
  return (CONFIRM_DETAIL_FIELDS[name] ?? [])
    .map(({ field, label }) => ({ label, value: alsText(input[field]) }))
    .filter((d): d is { label: string; value: string } => d.value !== null)
}

/** Ein Wert für die Anzeige: Text bleibt, eine Liste (die Fragen) wird zu einer Zeile. */
function alsText(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() !== '' ? value : null
  if (Array.isArray(value)) {
    const teile = value.filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    return teile.length > 0 ? teile.join(' · ') : null
  }
  return null
}

/**
 * Was der Besucher zu einer Antwort noch entscheiden muss: die Kontaktkarte
 * bei create_followup, sonst Ja/Nein mit den Angaben, die hinausgehen.
 *
 * Eigene Komponente, weil sie an zwei Stellen steht: unter der Sprechblase im
 * Chat (MessageBubble) und im Sprachmodus **angeheftet**, auch wenn die
 * Antwort, zu der sie gehört, schon aus dem Bild gewandert ist — die Karte
 * bleibt, bis sie abgeschickt oder mit „Doch nicht“ geschlossen ist.
 */
export function PendingAction({ message }: { message: UiMessage }) {
  const confirmTool = useChatStore((s) => s.confirmTool)
  const lastContact = useChatStore((s) => s.lastContact)
  const entwurf = useChatStore((s) =>
    message.pendingConfirmation
      ? s.kartenEntwuerfe[message.pendingConfirmation.toolCallId]
      : undefined,
  )
  const merkeKartenEntwurf = useChatStore((s) => s.merkeKartenEntwurf)
  const t = useT()
  const pending = message.pendingConfirmation
  if (!pending) return null

  if (pending.name === 'create_followup') {
    // Kontaktkarte statt Ja/Nein: Die Kontaktdaten kommen aus der Karte,
    // nicht aus dem Chat — das Handy füllt sie aus, die zweite Rückfrage
    // im selben Besuch findet sie schon vor. Ein angefangener Entwurf
    // überlebt das Aus- und Einhängen der Karte.
    return (
      <ContactCard
        key={pending.toolCallId}
        initial={entwurf ?? { ...lastContact, ...kontaktAusAufruf(pending.input) }}
        busy={message.confirming === true}
        error={message.confirmError ?? null}
        onChange={(data) => merkeKartenEntwurf(pending.toolCallId, data)}
        onSubmit={(data) =>
          void confirmTool(pending.toolCallId, true, {
            questions: anliegenAlsFragen(data.anliegen),
            name: data.name,
            company: data.company || undefined,
            phone: data.phone || undefined,
            email: data.email || undefined,
            preferredTime: data.preferredTime || undefined,
          })
        }
        onDecline={() => void confirmTool(pending.toolCallId, false)}
      />
    )
  }

  const details = confirmationDetails(pending.name, pending.input)
  return (
    <div className="mt-3 rounded-2xl bg-surface-2 p-4 text-sm">
      <p className="mb-3 font-semibold">{t.confirmLabels[pending.name] ?? t.confirmGeneric}</p>
      {details.length > 0 && (
        <dl className="mb-4 space-y-1 text-sm">
          {details.map((d) => (
            <div key={d.label} className="flex gap-2">
              <dt className="w-16 shrink-0 text-text-muted">{d.label}</dt>
              <dd className="break-all font-medium">{d.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="flex gap-2">
        <button
          className="rounded-full bg-accent px-4 py-2 font-semibold text-on-accent"
          onClick={() => void confirmTool(pending.toolCallId, true)}
        >
          {t.yesPlease}
        </button>
        <button
          className="rounded-full px-4 py-2 font-semibold text-text-muted hover:text-text"
          onClick={() => void confirmTool(pending.toolCallId, false)}
        >
          {t.noThanks}
        </button>
      </div>
    </div>
  )
}
